import { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { getAllSessions } from "../../../service/sessions"
import { getAllResultBreakdowns, sortBreakdownsByClass, type ResultBreakdown } from "../../../service/result-breakdown"
import { getAllClasses } from "../../../service/classes"
import { all_routes } from "../../router/all_routes"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"

type SessionOpt = { _id: string; title: string }
type ClassOpt = { _id: string; name: string; sections: { _id: string; name: string }[] }

const MarksTabulation = () => {
  const currentBranch = useSelector(selectCurrentBranch)
  const [sessions, setSessions] = useState<SessionOpt[]>([])
  const [breakdowns, setBreakdowns] = useState<ResultBreakdown[]>([])
  const [classes, setClasses] = useState<ClassOpt[]>([])
  const [filter, setFilter] = useState({ session: "", breakdown: "", class: "", section: "" })

  useEffect(() => {
    // Scopes to the current campus implicitly via the x-branch header (set
    // from whichever campus is active when this fires). Missing
    // currentBranch?._id in the deps meant switching campus in the top bar
    // AFTER this page loaded left the Session list stale from the OLD
    // campus -- same display text ("Aug 2026-2027" exists per-campus), so
    // picking it silently resolved to the wrong campus's session id while
    // Class/Section correctly used the new one, guaranteeing an empty
    // Section list downstream (same bug, same fix, as report-card/index.tsx).
    getAllSessions({ limit: 200, status: "active" }).then((r: any) => setSessions(r?.sessions || [])).catch(() => {})
    setFilter({ session: "", breakdown: "", class: "", section: "" })
  }, [currentBranch?._id])

  useEffect(() => {
    // A Class is shared across every campus running its System, but Sections
    // are branch-specific -- without branch here, the backend's own
    // sectionsMatchFor() has nothing to scope on and returns every campus's
    // same-named section mixed together (e.g. "A" showing up 2-3x with no
    // way to tell them apart), which is exactly what was reported live.
    // Session matters too -- Class.sections accumulates every session's
    // sections over the class's lifetime (never pruned), so a class with
    // multiple years of history shows that year's "A" alongside last
    // year's "A" unless session narrows it down as well.
    if (!currentBranch?._id || !filter.session) { setClasses([]); return }
    getAllClasses({ limit: 500, branch: currentBranch._id, session: filter.session })
      .then((r: any) => setClasses((r?.classes || []).map((c: any) => ({ _id: c._id, name: c.name, sections: c.sections || [] }))))
      .catch(() => setClasses([]))
  }, [currentBranch?._id, filter.session])

  useEffect(() => {
    if (!filter.session || !currentBranch?._id) { setBreakdowns([]); return }
    // status: "active" excluded every real breakdown -- a breakdown's
    // lifecycle runs draft -> active -> submitted, and once marks are
    // finalized it's "submitted", not "active" (same fix already applied
    // on the Marks Entry cascade for the same reason).
    //
    // branchId is required here, not optional: the backend expands `session`
    // to every OTHER campus's own session sharing the same display title
    // ("Aug 2026-2027" exists once per campus) -- that's intentional for the
    // Edit modal's cross-campus sibling lookup, but without branchId to narrow
    // it back down, this call silently returns every campus's breakdowns
    // mixed together (duplicate-looking titles, and classes/grades from
    // systems this campus doesn't even run), which is exactly what was
    // reported live on Eon (O-Level) showing Matric-named breakdowns.
    getAllResultBreakdowns({ session: filter.session, branchId: currentBranch._id, all: true, limit: 200 })
      .then(r => setBreakdowns(sortBreakdownsByClass(r?.data || []))).catch(() => setBreakdowns([]))
  }, [filter.session, currentBranch?._id])

  const setF = (k: keyof typeof filter, v: string) => {
    setFilter(s => {
      const next = { ...s, [k]: v }
      const order: (keyof typeof filter)[] = ["session", "breakdown", "class", "section"]
      const i = order.indexOf(k)
      for (let j = i + 1; j < order.length; j++) next[order[j]] = ""
      return next
    })
  }

  const selectedBreakdown = breakdowns.find(b => b._id === filter.breakdown)
  const breakdownClassIds = useMemo(() => new Set((selectedBreakdown?.classes || []).map(c => String(c._id))), [selectedBreakdown])
  const classOptions = useMemo(() => classes.filter(c => breakdownClassIds.size === 0 || breakdownClassIds.has(c._id)), [classes, breakdownClassIds])
  const sectionOptions = useMemo(() => classOptions.find(c => c._id === filter.class)?.sections || [], [classOptions, filter.class])

  const ready = !!(filter.breakdown && filter.class && filter.section)
  const sheetUrl = (autoprint: boolean) => {
    const qp = new URLSearchParams({ breakdown: filter.breakdown, class: filter.class, section: filter.section })
    if (autoprint) qp.set("autoprint", "1")
    return `${all_routes.marksTabulationSheet}?${qp.toString()}`
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="mb-3">
          <h3 className="page-title mb-1">Marks Tabulation</h3>
          <p className="text-muted mb-0">Class-wide consolidated result sheet. Pick the full scope — including section — and click Open or Print.</p>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label">Session</label>
                <select className="form-select" value={filter.session} onChange={e => setF("session", e.target.value)}>
                  <option value="">— Select session —</option>
                  {sessions.map(s => <option key={s._id} value={s._id}>{s.title}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Breakdown</label>
                <select className="form-select" value={filter.breakdown} disabled={!filter.session} onChange={e => setF("breakdown", e.target.value)}>
                  <option value="">{!filter.session ? "Pick a session first" : "— Select breakdown —"}</option>
                  {breakdowns.map(b => <option key={b._id} value={b._id}>{b.title}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Class</label>
                <select className="form-select" value={filter.class} disabled={!filter.breakdown} onChange={e => setF("class", e.target.value)}>
                  <option value="">{!filter.breakdown ? "Pick a breakdown first" : "— Select class —"}</option>
                  {classOptions.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Section</label>
                <select className="form-select" value={filter.section} disabled={!filter.class} onChange={e => setF("section", e.target.value)}>
                  <option value="">{!filter.class ? "Pick a class first" : "— Select section —"}</option>
                  {sectionOptions.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                </select>
                {!ready && <small className="text-muted">Required</small>}
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body d-flex align-items-center justify-content-between flex-wrap gap-2">
            <span className="text-muted">
              {ready
                ? "Tabulation is ready — opens in a new tab. Print uses A3 paper."
                : "Pick breakdown, class and section to load the result sheet."}
            </span>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={!ready}
                onClick={() => window.open(sheetUrl(false), "_blank")}
              >
                Open ↗
              </button>
              <button
                className="btn btn-dark btn-sm"
                disabled={!ready}
                onClick={() => window.open(sheetUrl(true), "_blank")}
              >
                Print ↗
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MarksTabulation
